// GoodWatch's logo: the G emblem is the first letter of the name, followed by a white "ood" and a yellow "Watch" in
// Gabarito 700 (design D of the wordmark prototype, see docs/implementation/goodwatch-wordmark.md). One component
// draws it in the browser and in the Satori exports (og images, share cards), so it has inline styles only, flex
// boxes only, no fragments, and the G is inline SVG instead of an image address.
import type { CSSProperties } from "react"

export const WORDMARK_WHITE = "#ffffff"
export const WORDMARK_YELLOW = "#fbbf24"
export const WORDMARK_INK = "#0a0a0a"

/** The font stack of `.brand-header` (main.css). Satori has no fallback names: pass `SATORI_FONT_FAMILY` there. */
export const BROWSER_FONT_FAMILY =
	'"Gabarito Brand", "Gabarito Fallback", "Gabarito Fallback Roboto", sans-serif'
export const SATORI_FONT_FAMILY = "Gabarito"

// The paths of app/img/goodwatch-logo-white.svg, with its two group transforms.
const G_WIDTH = 179.84172
const G_HEIGHT = 189.15848
const G_RING =
	"m 188.93136,184.69553 c -0.26723,3.42107 -0.45773,4.10369 -1.40759,5.50863 -0.64823,0.91281 -1.86531,1.93939 -3.00566,2.50825 l -1.86532,0.98954 -12.55712,0.11377 c -10.95904,0.11377 -12.74763,0.037 -14.19225,-0.49477 -3.61686,-1.3679 -5.40544,-4.02696 -5.44248,-7.94279 0,-2.43152 -0.68527,-3.64596 -2.01613,-3.64596 -0.381,0 -2.13254,0.75935 -3.8047,1.67217 -6.58284,3.57187 -16.93334,7.06702 -26.52184,8.92968 C 95.247695,196.8161 70.858404,191.45828 49.816092,177.43801 29.115092,163.6056 14.996926,141.45203 10.356134,115.42497 9.2898626,109.30516 8.9855916,94.485846 9.8216756,87.342096 12.030946,68.305326 19.373134,51.663034 32.120759,36.880764 42.204029,25.178243 54.914613,16.399368 70.096404,10.663202 76.869735,8.1152638 87.066775,5.7234307 93.877155,5.0752015 c 9.247185,-0.9101667 20.473455,-0.037042 29.908495,2.2807083 15.18444,3.7623752 31.35577,12.8058332 42.46562,23.7886872 4.29948,4.217458 7.95338,8.548688 8.52488,10.144125 0.60854,1.521354 0.49477,5.0165 -0.15346,6.307667 -0.94985,1.825625 -19.89931,18.354145 -22.71713,19.79877 -1.82562,0.949854 -4.60375,1.100667 -6.04837,0.341313 -0.49477,-0.267229 -2.8919,-2.394479 -5.28902,-4.712229 -7.95338,-7.675563 -13.8139,-11.437938 -22.26204,-14.210771 -6.39234,-2.129896 -9.2075,-2.547937 -16.93069,-2.547937 -6.278555,0 -6.887095,0.07673 -11.112495,1.217083 -10.38754,2.812521 -17.692687,7.14375 -25.000478,14.78227 -7.418917,7.712605 -12.062354,16.79575 -13.964709,27.130375 -0.949854,5.090583 -0.949854,15.732127 -0.03704,20.140087 2.397125,11.3612 7.913687,21.43125 16.02052,29.18089 4.831292,4.63815 12.366627,9.31069 18.150417,11.32417 5.28902,1.82562 8.97996,2.47121 15.409325,2.69875 7.38188,0.26458 11.72104,-0.34131 18.57111,-2.58498 11.26331,-3.68565 20.85181,-11.32417 27.47168,-21.88633 2.05582,-3.26761 2.47386,-5.0165 1.40759,-6.08013 -0.64559,-0.64558 -1.52136,-0.79904 -7.15434,-0.98689 -12.78995,0 -17.0815,0.11906 -28.0326,0.14552 l 20.10304,-13.52286 0.0476,-0.0344 c 1.70391,-1.19327 2.70668,-3.21205 2.68552,-5.40544 -0.0212,-2.16165 -1.04511,-4.1275 -2.73315,-5.26256 L 112.99859,83.524159 c 13.8086,0.153458 21.92337,0.550333 39.7457,0.550333 29.18619,0.1905 30.70755,0.227542 31.77382,0.912813 1.82562,1.140354 2.89189,2.203979 3.72798,3.839104 l 0.83608,1.518708 0.0767,45.217293 c 0.0767,24.88935 -0.037,46.96619 -0.22754,49.13312 z"
const G_PLAY =
	"m 454.39,509.36 -104.34,70.2 -55.06,37.04 V 401 l 53.45,35.96 106,71.32 c 0.08,0.31 0.06,0.8 -0.05,1.08 z"

// The header's lockup at 36px type: a 28px G on the baseline, 2px before "ood".
const G_EM = 28 / 36
const GAP_EM = 2 / 36
// Satori has no baseline alignment, so the parts sit on the bottom of the line box and the G is lifted to the
// baseline. At line height 1 that is Gabarito's descent (0.26 em) less half its extra leading (0.94 + 0.26 - 1).
const BASELINE_EM = 0.16
// The backing plate on light backgrounds: an ink chip that hugs the lockup. The colors never change.
const PLATE_EM = {
	top: 0.24,
	right: 0.42,
	bottom: 0.14,
	left: 0.34,
	radius: 0.26,
}

/** Total width over the type size, for fitting the wordmark into a fixed box. "oodWatch" sets 4.654 em wide. */
export const WORDMARK_WIDTH_EM = G_EM * (G_WIDTH / G_HEIGHT) + GAP_EM + 4.654
export const PLATE_WIDTH_EM = WORDMARK_WIDTH_EM + PLATE_EM.left + PLATE_EM.right

type Size = number | string

/** The G emblem alone, decorative. `height` in px, or any CSS length in the browser. */
export function GoodWatchG({
	height,
	color = WORDMARK_WHITE,
	className,
	style,
}: {
	height: Size
	color?: string
	className?: string
	style?: CSSProperties
}) {
	const width =
		typeof height === "number"
			? (height * G_WIDTH) / G_HEIGHT
			: `calc(${height} * ${G_WIDTH / G_HEIGHT})`
	return (
		<span
			aria-hidden
			className={className}
			style={{ display: "flex", flexShrink: 0, ...style }}
		>
			<svg
				aria-hidden="true"
				focusable="false"
				xmlns="http://www.w3.org/2000/svg"
				viewBox={`0 0 ${G_WIDTH} ${G_HEIGHT}`}
				width={typeof width === "number" ? width : undefined}
				height={typeof height === "number" ? height : undefined}
				style={{ display: "flex", width, height }}
			>
				<g fill={color} transform="translate(-9.3413446,-4.720956)">
					<path d={G_RING} />
					<path
						transform="matrix(0.26458333,0,0,0.26458333,9.3401336,-32.149027)"
						d={G_PLAY}
					/>
				</g>
			</svg>
		</span>
	)
}

export interface GoodWatchWordmarkProps {
	/** The type size: px as a number, or any CSS length in the browser ("1em"). The G is 28/36 of it. Default 36. */
	fontSize?: Size
	/** Sets the size by the G's height in px instead of the type size. */
	gHeight?: number
	/** Light background: the same white and yellow lockup on an ink plate. */
	plate?: boolean
	fontFamily?: string
	/** The root is a flex box, and inline styles win over classes: to hide it at a breakpoint, wrap it. */
	className?: string
	style?: CSSProperties
	/** Hooks for animating the G and "oodWatch" apart (the TV's boot screen). */
	gClassName?: string
	textClassName?: string
}

export function GoodWatchWordmark({
	fontSize,
	gHeight,
	plate = false,
	fontFamily = BROWSER_FONT_FAMILY,
	className,
	style,
	gClassName,
	textClassName,
}: GoodWatchWordmarkProps) {
	const size: Size = gHeight != null ? gHeight / G_EM : (fontSize ?? 36)
	// Satori gets plain px numbers. A CSS length sets the root's font size and everything else follows in em.
	const em = (factor: number) =>
		typeof size === "number" ? size * factor : `${factor}em`
	return (
		<span
			role="img"
			aria-label="GoodWatch"
			className={className}
			style={{
				display: "flex",
				flexShrink: 0,
				alignItems: "flex-end",
				fontFamily,
				fontWeight: 700,
				fontSize: size,
				fontStyle: "normal",
				lineHeight: 1,
				letterSpacing: 0,
				textTransform: "none",
				whiteSpace: "nowrap",
				...(plate && {
					paddingTop: em(PLATE_EM.top),
					paddingRight: em(PLATE_EM.right),
					paddingBottom: em(PLATE_EM.bottom),
					paddingLeft: em(PLATE_EM.left),
					borderRadius: em(PLATE_EM.radius),
					backgroundColor: WORDMARK_INK,
				}),
				...style,
			}}
		>
			<GoodWatchG
				height={em(G_EM)}
				className={gClassName}
				style={{ marginBottom: em(BASELINE_EM) }}
			/>
			<span
				aria-hidden
				className={textClassName}
				style={{ display: "flex", marginLeft: em(GAP_EM) }}
			>
				<span style={{ display: "flex", color: WORDMARK_WHITE }}>ood</span>
				<span style={{ display: "flex", color: WORDMARK_YELLOW }}>Watch</span>
			</span>
		</span>
	)
}
